import { Eye, EyeOff } from "lucide-react";
import { ReactNode, SyntheticEvent, useEffect, useRef, useState } from "react";

import {
  LoginThrottledError,
  getAvailableCredentials,
  resendTwoFactorCode,
  sendLoginForm,
  verifyTwoFactorCode,
} from "@/api/auth";
import { TwoFactorMethod } from "@/api/twoFactor";
import { GoogleIcon } from "@/assets/images";
import { Button } from "@/components/ui";
import { AuthCard } from "@/components/ui/AuthCard";
import { AuthOption } from "@/components/ui/AuthOption";
import { Divider } from "@/components/ui/Divider";
import { FormHeader } from "@/components/ui/FormHeader";
import { InlineError } from "@/components/ui/InlineError";
import { FRONT_DOOR_INPUT, Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { TextLink } from "@/components/ui/TextLink";
import { BusyButton } from "@/components/ui/buttons/BusyButton";
import { cn } from "@/lib/cn";
import { hasMessage } from "@/lib/error";

import { ChangeAccount } from "./ChangeAccount";

const PasswordResetLink = (props: {
  email: string;
  redirectUrl?: string | null;
  className?: string;
  children: ReactNode;
}) => (
  <TextLink
    href={
      "/reset" + (props.redirectUrl ? `?redirect_url=${encodeURIComponent(props.redirectUrl)}` : "")
    }
    className={props.className}
    onClick={() => {
      // Store email in sessionStorage for password reset form
      sessionStorage.setItem("password-reset-email", props.email);
    }}
  >
    {props.children}
  </TextLink>
);

/**
 * A step's error notice and submit button, busy while the step's request runs. The notice opens to
 * its height over 200ms rather than at once, so the centred card eases instead of jumping.
 */
const SubmitRow = (props: {
  label: string;
  busy: boolean;
  /** Announced while busy, since a screen reader hears nothing from the spinner */
  busyStatus: string;
  error: ReactNode;
}) => (
  <div>
    <div
      className={cn(
        "grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none",
        props.error ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
      )}
    >
      <div className="min-h-0 overflow-hidden">
        {props.error && (
          <div className="pb-6">
            <InlineError>{props.error}</InlineError>
          </div>
        )}
      </div>
    </div>
    <span role="status" className="sr-only">
      {props.busy ? props.busyStatus : ""}
    </span>
    <BusyButton type="submit" variant="brand" size="xl" className="w-full" busy={props.busy}>
      {props.label}
    </BusyButton>
  </div>
);

/** How long a navigation away may keep the button busy before the page assumes it was stopped */
const NAVIGATION_STALL_MS = 15_000;

/** Why a password login was turned away, for anything but a mismatch, which the field itself shows. */
const loginRefusal = (e: unknown, resetLink: ReactNode): ReactNode => {
  if (e instanceof LoginThrottledError) {
    const minutes =
      e.retryAfterSeconds === null ? null : Math.max(1, Math.ceil(e.retryAfterSeconds / 60));
    return minutes === null
      ? "Too many sign-in attempts. Wait a few minutes, then try again."
      : `Too many sign-in attempts. Wait ${minutes} ${minutes === 1 ? "minute" : "minutes"}, then try again.`;
  }
  switch (hasMessage(e) ? e.message : undefined) {
    case "ACCOUNT_LOCKED":
      // No wait time: the server does not say when the lock lifts, and from the 100th failure it never does
      return (
        <>
          This account is locked after too many failed sign-in attempts. {resetLink} to unlock it,
          or ask your administrator.
        </>
      );
    case "ACCOUNT_DEACTIVATED":
      return "This account is deactivated, so it can't sign in. Ask your administrator if you still need access.";
    default:
      // Never the error's own text: past the codes above it is a server or network message
      return "We couldn't sign you in just now. Try again in a moment.";
  }
};

export const LoginForm = (params: {
  initialEmail?: string | null;
  redirectUrl?: string | null;
  errorMessage?: string | null;
}) => {
  const { initialEmail, redirectUrl, errorMessage } = params;

  //fields
  const [email, setEmail] = useState(initialEmail || "");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [ssoUrl, setSsoUrl] = useState("");
  // const [remember, setRemember] = useState(false);
  const [error, setError] = useState<ReactNode>(errorMessage || "");
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  // A password login learns this from its own XHR response; an SSO login is stopped by a redirect,
  // so the server says so in the URL instead.
  const initialParams = new URLSearchParams(window.location.search);
  const [mfaRequired, setMfaRequired] = useState(initialParams.get("mfa") === "required");
  const [mfaMethod, setMfaMethod] = useState<TwoFactorMethod>(
    initialParams.get("method") === "EMAIL" ? "EMAIL" : "TOTP",
  );
  const [emailHint, setEmailHint] = useState<string | null>(null);
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent" | "exhausted">("idle");
  const [code, setCode] = useState("");
  const [spentBackupCode, setSpentBackupCode] = useState<{ location: string; left: number } | null>(
    null,
  );

  const [authOption, setAuthOption] = useState("PASSWORD");
  const passwordInputRef = useRef<HTMLInputElement | null>(null);

  // A request of the current step is running. It stays set once the page is navigating away (to the
  // product, to the IdP), so the button holds its spinner until the next page replaces this one, or
  // until `leavePage` gives up on it. The ref refuses a second submit before the re-render that
  // disables the button.
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const startRequest = () => {
    if (busyRef.current) {
      return false;
    }
    busyRef.current = true;
    setBusy(true);
    return true;
  };
  const endRequest = () => {
    busyRef.current = false;
    setBusy(false);
  };
  // A navigation that never completes (Esc, Stop, an IdP that does not answer) fires no event and
  // leaves this page up, so the spinner gives way after a while and the button can start it again
  const leavePage = (url: string) => {
    window.location.href = url;
    setTimeout(endRequest, NAVIGATION_STALL_MS);
  };

  // Back from the IdP or the product can restore this page from the back/forward cache, with the
  // spinner still on; a fresh render is not involved, so only `pageshow` says it happened
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        endRequest();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const checkCredentials = (login: string) => {
    if (!startRequest()) {
      return;
    }
    // An SSO-only account is redirected inside, and the button keeps its spinner until the IdP loads
    getAvailableCredentials(login, redirectUrl, {
      onPasswordNeeded: () => {
        endRequest();
        setShowPassword(true);
        if (passwordInputRef.current) {
          passwordInputRef.current.focus();
        }
      },
      onSsoUrl: (url) => {
        setSsoUrl(url);
        setAuthOption("PASSWORD_SSO");
      },
      onSsoOnly: leavePage,
      onFailed: () => {
        endRequest();
        setError("We couldn't sign you in just now. Try again in a moment.");
      },
    });
  };

  // FIXME: ideally this should be executed before AuthForm gets rendered,
  //        to avoid unnecessary flash of UI when user is redirected to SSO
  useEffect(() => {
    if (initialEmail) {
      checkCredentials(initialEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSubmit(event: SyntheticEvent) {
    event.preventDefault();

    if (showPassword) {
      if (authOption === "PASSWORD_SSO") {
        if (startRequest()) {
          leavePage(ssoUrl);
        }
      } else {
        loginWithPassword();
      }
    } else {
      // A failed check from the previous try must not outlive a retry that succeeds
      setError("");
      checkCredentials(email);
    }
  }

  const loginWithPassword = async () => {
    if (!startRequest()) {
      return;
    }
    try {
      const result = await sendLoginForm(email, password, redirectUrl);
      if (result.status === "OK") {
        leavePage(result.location);
      } else {
        endRequest();
        setMfaMethod(result.method);
        setEmailHint(result.emailHint);
        setResendState("idle");
        setMfaRequired(true);
        setError("");
      }
    } catch (e) {
      endRequest();
      if (hasMessage(e) && e.message === "BAD_CREDENTIALS") {
        setError("");
        setPassword("");
        setPasswordMismatch(true);
        passwordInputRef.current?.focus();
      } else {
        setPasswordMismatch(false);
        setError(
          loginRefusal(
            e,
            <PasswordResetLink email={email} redirectUrl={redirectUrl}>
              Reset your password
            </PasswordResetLink>,
          ),
        );
      }
    }
  };

  const submitCode = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!startRequest()) {
      return;
    }
    try {
      const verified = await verifyTwoFactorCode(code, redirectUrl);
      if (verified.factor === "backup code") {
        endRequest();
        setSpentBackupCode({ location: verified.location, left: verified.backupCodesRemaining });
        return;
      }
      leavePage(verified.location);
    } catch (e) {
      endRequest();
      setCode("");
      setError(
        hasMessage(e) && e.message === "MFA_FAILED"
          ? mfaMethod === "EMAIL"
            ? "That code did not work. Check the latest email, or use one of your backup codes."
            : "That code did not work. Try again, or use one of your backup codes."
          : "Verification failed",
      );
    }
  };

  const resend = async () => {
    setError("");
    setResendState("sending");
    try {
      await resendTwoFactorCode();
      setResendState("sent");
    } catch (e) {
      if (hasMessage(e) && e.message === "RESEND_LIMIT") {
        setResendState("exhausted");
        setError(
          "No more codes can be sent for this sign-in. Wait for the last one, use a backup code, or start again.",
        );
      } else if (hasMessage(e) && e.message === "RESEND_EXPIRED") {
        setResendState("exhausted");
        setError("This sign-in has expired. Enter your password again to start over.");
      } else {
        setResendState("idle");
        setError("The code could not be sent. Try again in a moment.");
      }
    }
  };

  if (spentBackupCode) {
    return (
      <AuthCard className="space-y-8">
        <FormHeader title="You used a backup code" />
        <p className="text-center text-sm text-muted-foreground">
          You have {spentBackupCode.left} left. Each one works once — you can issue a new set from
          Account security.
        </p>
        <Button
          type="button"
          variant="brand"
          size="xl"
          className="w-full"
          onClick={() => {
            window.location.href = spentBackupCode.location;
          }}
        >
          Continue
        </Button>
      </AuthCard>
    );
  }

  if (mfaRequired) {
    return (
      <AuthCard as="form" onSubmit={submitCode} className="space-y-8">
        <FormHeader title="Two-step verification" />

        {/* An SSO login lands here straight from `/login?mfa=required`, with no email to show. */}
        {email && (
          <ChangeAccount
            email={email}
            onClick={() => {
              setMfaRequired(false);
              setCode("");
              setError("");
              setResendState("idle");
              setEmailHint(null);
            }}
          />
        )}

        {mfaMethod === "EMAIL" && (
          <p className="text-center text-sm text-muted-foreground">
            {emailHint
              ? `We sent a code to ${emailHint}. It works for 10 minutes.`
              : "We sent a code to your email address. It works for 10 minutes."}
          </p>
        )}

        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="mfa-code" className="text-sm font-semibold">
              Verification code
            </Label>
            <Input
              id="mfa-code"
              autoFocus
              autoComplete="one-time-code"
              inputMode={mfaMethod === "EMAIL" ? "numeric" : "text"}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={FRONT_DOOR_INPUT}
              placeholder="123456"
            />
          </div>

          <SubmitRow label="Verify" busy={busy} busyStatus="Checking the code…" error={error} />

          {mfaMethod === "EMAIL" ? (
            <div className="space-y-2 text-center text-sm text-muted-foreground">
              {resendState === "sent" && <p>A new code is on its way.</p>}
              {resendState !== "exhausted" && (
                <button
                  type="button"
                  className="min-h-11 min-w-11 underline hover:text-foreground"
                  disabled={resendState === "sending"}
                  onClick={resend}
                >
                  Send a new code
                </button>
              )}
              <p>
                Can&apos;t reach your email? Use a backup code in the same field. If you have
                neither, contact your administrator.
              </p>
            </div>
          ) : (
            <p className="text-center text-sm text-muted-foreground">
              Lost your device? Enter one of your backup codes in the same field. If you have
              neither, contact your administrator.
            </p>
          )}
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard as="form" onSubmit={handleSubmit} className="space-y-8">
      {!showPassword && (
        <>
          <FormHeader title="Welcome back!" subtitle="Enter your email to access your account" />

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold">
                E-mail
              </Label>
              <Input
                id="email"
                autoFocus
                required
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => {
                  setPassword("");
                  setEmail(e.target.value);
                }}
                className={FRONT_DOOR_INPUT}
                placeholder="your.email@example.com"
              />
            </div>

            <SubmitRow label="Next" busy={busy} busyStatus="Checking your email…" error={error} />

            <Divider />

            <Button
              type="button"
              variant="outline"
              size="xl"
              // Google's button stays neutral by its brand rules, so it drops the outline's violet
              className="w-full gap-3 border-border text-foreground hover:border-subtle-foreground hover:bg-muted hover:text-foreground active:border-subtle-foreground active:bg-muted"
              onClick={() => {
                window.location.href =
                  "/oauth2/authorization/google" +
                  (redirectUrl ? `?redirect_url=${encodeURIComponent(redirectUrl)}` : "");
              }}
            >
              <GoogleIcon width={20} height={20} />
              Sign in with Google
            </Button>
          </div>
        </>
      )}

      {showPassword && (
        <>
          <FormHeader title="Hello again" subtitle="Enter your password for:" />

          {/*
            fix: Provide correct email for password managers
            Username (email) and password inputs must be displayed together so that
            password managers can correctly associate both values.
            Unfortunately username must be a text input, not hidden, see:
            https://stackoverflow.com/questions/52612255/chrome-stores-the-new-password-but-does-not-link-it-to-the-current-username-how
          */}
          <input
            type="text"
            autoComplete="username"
            value={email}
            readOnly
            style={{ display: "none" }}
          />

          <ChangeAccount
            email={email}
            onClick={() => {
              setShowPassword(false);
              setError("");
              setPasswordMismatch(false);
            }}
          />

          {ssoUrl && (
            <fieldset className="space-y-2">
              <legend className="sr-only">Sign in with</legend>
              <AuthOption
                name="authOption"
                title="Single sign-on"
                subtitle="Sign in through your company's identity provider."
                isSelected={authOption === "PASSWORD_SSO"}
                onSelect={() => setAuthOption("PASSWORD_SSO")}
              />
              <AuthOption
                name="authOption"
                title="Password"
                subtitle="Your password, kept for administrators in case single sign-on is down."
                isSelected={authOption === "PASSWORD"}
                onSelect={() => setAuthOption("PASSWORD")}
              />
            </fieldset>
          )}

          <div className="space-y-6">
            {authOption === "PASSWORD" && (
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-semibold">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    ref={passwordInputRef}
                    id="password"
                    autoFocus
                    type={passwordVisible ? "text" : "password"}
                    autoComplete="current-password"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      setPasswordMismatch(false);
                      setPassword(e.target.value);
                    }}
                    aria-invalid={passwordMismatch || undefined}
                    aria-describedby={passwordMismatch ? "password-error" : undefined}
                    className={cn(
                      FRONT_DOOR_INPUT,
                      "pr-12 aria-invalid:border-destructive-border aria-invalid:focus-visible:ring-destructive-border",
                    )}
                  />
                  {/* Typing a password blind on a phone keyboard is where most mismatches start */}
                  <button
                    type="button"
                    aria-label="Show password"
                    aria-pressed={passwordVisible}
                    aria-controls="password"
                    onClick={() => setPasswordVisible((visible) => !visible)}
                    className="absolute inset-y-0 right-1 my-auto flex size-10 cursor-pointer items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring pointer-coarse:size-11"
                  >
                    {passwordVisible ? (
                      <EyeOff aria-hidden={true} size={20} strokeWidth={1.5} />
                    ) : (
                      <Eye aria-hidden={true} size={20} strokeWidth={1.5} />
                    )}
                  </button>
                </div>
                {/* The error reads first, left-aligned under the field; the way out stays on the right */}
                <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
                  {passwordMismatch && (
                    <p
                      id="password-error"
                      role="alert"
                      className="text-xs font-medium text-pretty text-destructive-fg"
                    >
                      That email and password don&apos;t match. Try again.
                    </p>
                  )}
                  <PasswordResetLink
                    email={email}
                    redirectUrl={redirectUrl}
                    // 14px, and on touch its line grows to a 44px target
                    className="ml-auto text-sm pointer-coarse:py-3"
                  >
                    Forgot your password?
                  </PasswordResetLink>
                </div>
              </div>
            )}

            <SubmitRow
              label="Log in"
              busy={busy}
              busyStatus={
                authOption === "PASSWORD_SSO"
                  ? "Taking you to your company's sign-in…"
                  : "Signing you in…"
              }
              error={error}
            />
          </div>
        </>
      )}
    </AuthCard>
  );
};
